import React from "react";
import { _goToReminder } from "routers/helper";

import { View, StyleSheet, TouchableOpacity } from "react-native";
import DeleteAlertComponent from "components/DeleteAlertComponent";
import ModalOPtions from "components/modals/ModalOPtions";
import ConfirmAlert from "components/ConfirmAlert";
import LoadingBox from "components/boxes/LoadingBox";
import Icon from "components/Icon";
import Text from "components/Text";

import { colors, sizes } from "assets/constants";
import { useFlag } from "hooks";

import { useDeleteReminder, useEditReminder } from "clients/apis";
import { useRecoilValue } from "recoil";
import { authAtom } from "stores";
import css from "styles";
import tinycolor from "tinycolor2";
import Shadow from "./Shadow";

export default function ReminderBox(props: {
  reminder: ReminderT;
  actions?: Array<"delete" | "archives">;
  dateMode?: "none" | "time" | "complete";
  refreshList?: () => void;
}) {
  const {
    reminder: {
      id,
      title,
      type_color,
      type_title,
      admin,
      customer,
      status,
      status_title,
      remind_date,
      remind_time,
    },
    actions,
    dateMode,
    refreshList,
  } = props;

  const {
    company: { color },
  } = useRecoilValue(authAtom);

  const { flg: alert, on: showAlert, off: hideAlert } = useFlag();
  const { flg: options, on: showOptions, off: hideOptions } = useFlag();

  const editReminderMut = useEditReminder();
  const deleteReminderMut = useDeleteReminder();

  const _onArchive = () => {
    hideOptions();
    const postData: EditReminderReqT = { title, status: "FOLLOW" };
    editReminderMut.mutate(
      { id, data: postData },
      { onSuccess: refreshList && refreshList }
    );
  };

  const _onDelete = () => {
    hideOptions();
    showAlert();
  };
  const _onDeleteConfirm = (close: () => void) => {
    close();
    deleteReminderMut.mutate(id, { onSuccess: refreshList && refreshList });
  };

  const _goToEdit = () => _goToReminder({ data: props.reminder });

  const _renderDateSection = () => {
    if (dateMode === "none") {
      return <></>;
    }

    if (dateMode === "time") {
      return (
        <View style={css.rRowCenterAlign}>
          <Icon name="clock" size={13} color={color} />
          <Text
            style={{ marginRight: sizes.iconTextSpace }}
            weight="bold"
            size={"sm"}
            color={colors.text.gray}
          >
            {remind_time}
          </Text>
        </View>
      );
    }

    return (
      <View style={css.rRowCenterAlign}>
        <Icon name="calendar" size={13} color={color} />
        <Text
          style={{ marginRight: sizes.iconTextSpace, marginLeft: 10 }}
          weight="bold"
          size={"sm"}
          color={colors.text.gray}
        >
          {remind_date}
        </Text>

        <Icon name="clock" size={13} color={color} />
        <Text
          style={{ marginRight: sizes.iconTextSpace }}
          weight="bold"
          size={"sm"}
          color={colors.text.gray}
        >
          {remind_time}
        </Text>
      </View>
    );
  };

  return (
    <>
      <TouchableOpacity
        onPress={_goToEdit}
        onLongPress={actions && actions?.length > 0 ? showOptions : undefined}
        activeOpacity={sizes.tapOpacity}
      >
        <Shadow style={{ ...styles.container, backgroundColor: colors.white }}>
          <View style={styles.header}>
            <Text
              size="lg"
              weight="medium"
              numberOfLines={1}
              color={colors.textColorDark}
              style={{ lineHeight: 20, top: -3 }}
            >
              {title}
            </Text>

            <Icon style={{ left: -5 }} size={22} name="chevron-left" />
          </View>

          <View style={styles.middle}>
            <View style={styles.row}>
              <View
                style={{
                  width: 10,
                  height: 10,
                  borderRadius: 12,
                  backgroundColor: tinycolor(type_color).lighten(20).toString(),
                }}
              />
              <Text color={colors.textColorGray} style={{ bottom: 1 }}>
                {" "}
                {type_title}{" "}
              </Text>
            </View>

            <View style={{ ...styles.row, ...styles.status }}>
              <View
                style={{
                  width: 10,
                  height: 10,
                  borderRadius: 10,
                  backgroundColor: tinycolor(
                    status === "FOLLOW" ? colors.success : colors.darkWarning
                  )
                    .lighten(40)
                    .toString(),
                }}
              />
              <Text
                style={{ bottom: 2 }}
                size={"xs"}
                weight="bold"
                color={
                  status === "FOLLOW" ? colors.success : colors.darkWarning
                }
              >
                {" "}
                {status_title}{" "}
              </Text>
            </View>
          </View>

          <View style={styles.footer}>
            <Icon color={color} size={12} type="fontAwesome" name="user-o" />
            <View
              style={{ ...styles.growContainer, marginRight: sizes.space / 2 }}
            >
              <Text numberOfLines={1} weight="medium">
                {admin?.name || customer?.name}
              </Text>
            </View>
            <View style={{ width: 15 }} />

            {_renderDateSection()}
          </View>
        </Shadow>
      </TouchableOpacity>

      <ModalOPtions
        flag={options}
        hide={hideOptions}
        onDelete={actions?.includes("delete") ? _onDelete : undefined}
        onArchive={actions?.includes("archives") ? _onArchive : undefined}
      />

      <ConfirmAlert
        force
        show={alert}
        hide={hideAlert}
        renderContent={(close) => (
          <DeleteAlertComponent
            remove={() => _onDeleteConfirm(close)}
            cancel={close}
          />
        )}
      />

      {(editReminderMut?.isLoading || deleteReminderMut?.isLoading) && (
        <LoadingBox />
      )}
    </>
  );
}

const styles = StyleSheet.create({
  container: {
    width: "100%",
    overflow: "hidden",
    padding: sizes.space,
    borderRadius: sizes.radius.xl,
  },

  header: {
    alignItems: "center",
    flexDirection: "row-reverse",
    justifyContent: "space-between",
  },

  middle: {
    flexDirection: "row-reverse",
    alignItems: "center",
    marginVertical: sizes.space,
  },

  row: { flexDirection: "row-reverse", alignItems: "center" },

  status: {
    marginLeft: sizes.space,
    paddingHorizontal: 5,
  },

  titleContainer: {
    flex: 1,
    alignItems: "flex-end",
  },

  footer: {
    flexDirection: "row-reverse",
    alignItems: "center",
    justifyContent: "space-between",
  },

  growContainer: { flexGrow: 1 },
});
